import { useMemo } from "react";
import type { CashierTransactionView } from "@/api/cashier";
import { EnumCashierTxStatus } from "@/api/cashier/types.gen";
import type { SelectedValuesType } from "../types";

const ALL = "all" as const;

const getTimeFilterValue = (timeValue: string, date: Date) => {
  const today = new Date();
  const transactionDate = new Date(date);

  switch (timeValue) {
    case "today": {
      return transactionDate.toDateString() === today.toDateString();
    }
    case "thisWeek": {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      weekStart.setHours(0, 0, 0, 0);
      return transactionDate >= weekStart;
    }
    case "thisMonth": {
      return (
        transactionDate.getMonth() === today.getMonth() &&
        transactionDate.getFullYear() === today.getFullYear()
      );
    }
    case "thisYear": {
      return transactionDate.getFullYear() === today.getFullYear();
    }
    default: {
      return true;
    }
  }
};

const statusToTransactionStatus: Record<
  string,
  EnumCashierTxStatus[] | typeof ALL
> = {
  requested: [EnumCashierTxStatus.CashierTxRequested],
  pending: [EnumCashierTxStatus.CashierTxPending],
  processing: [EnumCashierTxStatus.CashierTxProcessing],
  complete: [EnumCashierTxStatus.CashierTxComplete],
  unsuccessful: [EnumCashierTxStatus.CashierTxUnsuccessful],
  cancelled: [EnumCashierTxStatus.CashierTxCancelled],
  [ALL]: ALL,
} as const;

type UseFilteredTransactionsProps = {
  transactions: CashierTransactionView[];
  selectedFilterValues: SelectedValuesType | null;
};

export const useFilteredTransactions = ({
  transactions,
  selectedFilterValues,
}: UseFilteredTransactionsProps) => {
  return useMemo(() => {
    if (!selectedFilterValues) return transactions;

    return transactions.filter((transaction: CashierTransactionView) => {
      const matchesTime =
        selectedFilterValues.time.value === ALL ||
        getTimeFilterValue(
          selectedFilterValues.time.value,
          transaction.created_at
        );

      const matchesType =
        selectedFilterValues.transaction.value === ALL ||
        transaction.type === selectedFilterValues.transaction.value;

      // Map UI status to actual transaction status for comparison
      const selectedStatus = selectedFilterValues.status.value;
      const matchesStatus =
        selectedStatus === ALL ||
        (Array.isArray(statusToTransactionStatus[selectedStatus]) &&
          statusToTransactionStatus[selectedStatus].includes(
            transaction.status
          ));

      const matchesAccount =
        selectedFilterValues.account.value === ALL ||
        transaction.from_trading_account ===
          selectedFilterValues.account.value ||
        transaction.to_trading_account === selectedFilterValues.account.value;

      const matchesPaymentMethod =
        selectedFilterValues.paymentMethod.value === ALL ||
        transaction.saved_payment_account?.name ===
          selectedFilterValues.paymentMethod.value;

      const matchesCurrency =
        selectedFilterValues.currency.value === ALL ||
        transaction.money.code === selectedFilterValues.currency.value;

      return (
        matchesTime &&
        matchesType &&
        matchesStatus &&
        matchesAccount &&
        matchesPaymentMethod &&
        matchesCurrency
      );
    });
  }, [transactions, selectedFilterValues]);
};
